'use client';

import { useEffect } from 'react';

// Auto-reloads the page once when a Next.js chunk fails to load — a deploy
// removed the file or a slow mobile network timed out — instead of leaving the
// visitor on a dead page (on the embedded widget: an invisible one). The
// sessionStorage timestamp throttles to one automatic reload per minute per
// tab; if storage is blocked we cannot bound a loop, so we never reload.

const RELOAD_AT_KEY = 'chunk-reload-at';
const RELOAD_THROTTLE_MS = 60_000;
const CHUNK_MESSAGE = /Loading (?:CSS )?chunk .+ failed/;

type ReloadDeps = {
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  now?: () => number;
  reload?: () => void;
};

function isChunkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const { name, message } = err as { name?: unknown; message?: unknown };
  if (name === 'ChunkLoadError') return true;
  return typeof message === 'string' && CHUNK_MESSAGE.test(message);
}

// Returns true when the error is a chunk failure being handled by a reload —
// callers should then skip their own error reporting for it.
export function maybeReloadForChunkError(err: unknown, deps: ReloadDeps = {}): boolean {
  if (!isChunkError(err)) return false;
  const now = deps.now ?? Date.now;
  const reload = deps.reload ?? (() => window.location.reload());
  try {
    const storage = deps.storage ?? window.sessionStorage;
    const last = Number(storage.getItem(RELOAD_AT_KEY));
    if (last && now() - last <= RELOAD_THROTTLE_MS) return false;
    storage.setItem(RELOAD_AT_KEY, String(now()));
  } catch {
    return false;
  }
  reload();
  return true;
}

export function ChunkReloadGuard() {
  useEffect(() => {
    // Navigation-time chunk failures surface as unhandled rejections,
    // hydration-time ones as uncaught errors; the exception itself lives on
    // event.error / event.reason, not the event object.
    const onError = (event: ErrorEvent) => {
      maybeReloadForChunkError(event.error);
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      maybeReloadForChunkError(event.reason);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
